import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

interface DailyAyah {
  arabic: string;
  translation: string;
  surah: string;
  surahNum: number;
  ayah: number;
  theme: string;
}

const DAILY_POOL: DailyAyah[] = [
  {
    arabic: "إِنَّ مَعَ ٱلْعُسْرِ يُسْرًا",
    translation: "Indeed, with hardship comes ease.",
    surah: "Ash-Sharh", surahNum: 94, ayah: 6,
    theme: "Hope & Patience",
  },
  {
    arabic: "وَمَن يَتَوَكَّلْ عَلَى ٱللَّهِ فَهُوَ حَسْبُهُۥ",
    translation: "And whoever relies upon Allah — then He is sufficient for him.",
    surah: "At-Talaq", surahNum: 65, ayah: 3,
    theme: "Trust in Allah",
  },
  {
    arabic: "رَبِّ زِدْنِى عِلْمًا",
    translation: "My Lord, increase me in knowledge.",
    surah: "Ta-Ha", surahNum: 20, ayah: 114,
    theme: "Seeking Knowledge",
  },
  {
    arabic: "وَلَسَوْفَ يُعْطِيكَ رَبُّكَ فَتَرْضَىٰ",
    translation: "And your Lord is going to give you, and you will be satisfied.",
    surah: "Ad-Duha", surahNum: 93, ayah: 5,
    theme: "Divine Promise",
  },
  {
    arabic: "فَٱذْكُرُونِىٓ أَذْكُرْكُمْ",
    translation: "So remember Me; I will remember you.",
    surah: "Al-Baqarah", surahNum: 2, ayah: 152,
    theme: "Remembrance",
  },
  {
    arabic: "وَهُوَ مَعَكُمْ أَيْنَ مَا كُنتُمْ",
    translation: "And He is with you wherever you are.",
    surah: "Al-Hadid", surahNum: 57, ayah: 4,
    theme: "Divine Nearness",
  },
  {
    arabic: "لَا يُكَلِّفُ ٱللَّهُ نَفْسًا إِلَّا وُسْعَهَا",
    translation: "Allah does not burden a soul beyond that it can bear.",
    surah: "Al-Baqarah", surahNum: 2, ayah: 286,
    theme: "Mercy & Ease",
  },
];

/**
 * Returns a consistent ayah for the current day (rotates daily).
 */
function getDailyIndex(): number {
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000,
  );
  return dayOfYear % DAILY_POOL.length;
}

/** The verse of the day as a full-width landing section. */
export default function DailyVerse() {
  const ayah = useMemo(() => DAILY_POOL[getDailyIndex()], []);

  return (
    <section className="sheet-paper sheet-lift py-28 md:py-40" aria-labelledby="daily-verse-label">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-[1100px] mx-auto px-5 md:px-8 text-center"
      >
        <span id="daily-verse-label" className="eyebrow eyebrow-center">
          Verse of the day · {ayah.theme}
        </span>

        <p
          lang="ar"
          dir="rtl"
          className="mt-10 font-arabic text-[clamp(2.75rem,7vw,6rem)] leading-[1.6] text-[var(--paper-ink)]"
        >
          {ayah.arabic}
        </p>

        <p className="mt-4 mx-auto max-w-[32ch] font-display italic text-[clamp(1.35rem,2.6vw,2.1rem)] leading-[1.35] text-[var(--paper-ink)]/75">
          &ldquo;{ayah.translation}&rdquo;
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3 text-[12px] font-semibold uppercase tracking-[0.18em]">
          <span className="text-[var(--paper-muted)]">
            Surah {ayah.surah} · {ayah.surahNum}:{ayah.ayah}
          </span>
          <Link
            to={`/quran/${ayah.surahNum}`}
            className="group inline-flex items-center gap-1.5 text-[var(--color-gold-dim)] dark:text-[var(--color-gold)] hover:text-[var(--paper-ink)] transition-colors"
          >
            Read the surah <ChevronRight className="btn-arrow w-3.5 h-3.5" />
          </Link>
        </div>
      </motion.div>
    </section>
  );
}
